import { useMemo } from "react";

/**
 * Native `<input type="datetime-local">` wrapper.
 *
 * Replaces the MUI X DateTimePicker we used to host behind a custom modal.
 * On iOS Safari the browser renders a native wheel picker that plays nicely
 * with Telegram's keyboard-avoidance; on Android / desktop it falls back to
 * the built-in date+time pair.
 *
 * The control exchanges ISO-8601 UTC strings with the form (what the API
 * wants), but displays values in the user's local zone — that's what the
 * native input speaks. The input value format is `YYYY-MM-DDTHH:mm` with no
 * timezone suffix.
 */
export function DateTimeInput({
  value,
  onChange,
  min,
  required,
}: {
  /** ISO-8601 string (UTC) or null. */
  value: string | null;
  onChange: (iso: string | null) => void;
  /** ISO-8601 string — native input will clamp anything earlier. */
  min?: string;
  required?: boolean;
}) {
  const localValue = useMemo(() => isoToLocalInput(value), [value]);
  const localMin = useMemo(() => (min ? isoToLocalInput(min) : undefined), [min]);

  return (
    <input
      type="datetime-local"
      value={localValue}
      min={localMin}
      required={required}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v ? localInputToIso(v) : null);
      }}
      style={{
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-md)",
        padding: "10px 14px",
        fontFamily: "var(--font-body)",
        fontSize: 15,
        background: "var(--bg)",
        color: "var(--fg1)",
        outline: "none",
        width: "100%",
        boxSizing: "border-box",
      }}
    />
  );
}

/**
 * `<input type="datetime-local">` speaks `YYYY-MM-DDTHH:mm` in the user's
 * local zone. Our form state is ISO UTC, so drop the seconds/ms and the
 * trailing `Z`, then pad the parts using the local clock.
 */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function localInputToIso(local: string): string {
  // `new Date("2026-04-23T14:30")` is parsed as local time by spec,
  // so `.toISOString()` converts back to UTC for the API.
  const d = new Date(local);
  return d.toISOString();
}
